import axios from 'axios';
import { computed, ref } from 'vue';

import useAwsPartnerCentral from '@/composables/useAwsPartnerCentral';
import useHubspotExportScope from '@/composables/useHubspotExportScope';
import {
  ACTIVE_FEED_STATUS,
  CONNECTION_CHECK_ERROR_STATUS,
  DELETING_FEED_STATUS,
  DELETION_FAILED_FEED_STATUS,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  PENDING_FEED_STATUS,
  RECORD_LIMIT_EXCEEDED_STATUS,
  REQUIRED_FEED_STATUS,
  SOURCE_FAILED_STATUS,
} from '@/constants/data_sources';
import { useFeedsStore, useSourcesStore } from '@/stores';
import urls from '@/urls';
import { returnTrimmedDomain } from '@/utils';

/* The simplified statuses that are emitted out of this
 * composable, mapped from the possible feed statuses */
export const statuses = {
  ERROR: 'ERROR',
  WARNING: 'WARNING',
  PAUSED: 'PAUSED',
  SETTING_UP: 'SETTING_UP',
  REQUIRE_SOURCES_SELECTION: 'NOT_SYNCED',
  ACTIVE: 'ACTIVE',
  DELETING: 'DELETING',
  DELETION_FAILED: 'DELETION_FAILED',
};

/**
 * Builds the reauthorize handler: it posts the authorize request for the feed
 * and sends the browser to the returned authorization URL. `formProperties`
 * changes a connection setting, such as the Salesforce proxy URL, along with
 * the reauthorization. The authorize schema is closed and a missing key keeps
 * the stored value, so the key is absent unless given.
 */
function reauthorizeFeed(loading, optionalScopesFor) {
  return async function reauthorize(feed, formProperties) {
    loading.value = true;
    try {
      const resp = await axios.post(urls.oauth.authorize, {
        params: {
          integration_type: feed.integration.type,
          feed_id: feed.id,
          ...optionalScopesFor(feed.integration.type),
          ...(formProperties ? { form_properties: formProperties } : {}),
        },
      });
      window.open(resp.data.authorization_url, '_self');
    } finally {
      loading.value = false;
    }
  };
}

const SEVERITY_BY_TEXT_COLOR = {
  'text-success-text-strong': 'success',
  'text-warning-text-strong': 'warning',
  'text-danger-text-strong': 'error',
};

const createFeedStatusUiObject = (
  text,
  bgColor,
  statusBgColor,
  textColor,
  type,
) => ({
  text,
  bgColor,
  statusBgColor,
  textColor,
  type,
  severity: SEVERITY_BY_TEXT_COLOR[textColor],
});

/**
 * Maps a feed onto the status object rendered wherever we surface feed health.
 * Sources are only consulted for Google Sheets row errors.
 *
 * @typedef {{ text?: string, bgColor?: string, statusBgColor?: string,
 *   textColor?: string, type?: string,
 *   severity?: 'success' | 'warning' | 'error' }} FeedStatusUi
 * @returns {FeedStatusUi}
 */
export function getFeedStatusUi(feed, sources = []) {
  switch (feed?.status) {
    case DELETING_FEED_STATUS:
      return createFeedStatusUiObject(
        'Deleting',
        'bg-danger-accent',
        'bg-danger-background-weak',
        'text-danger-text-strong',
        statuses.DELETING,
      );
    case DELETION_FAILED_FEED_STATUS:
      return createFeedStatusUiObject(
        'Deletion Failed',
        'bg-warning-accent',
        'bg-warning-background-weak',
        'text-warning-text-strong',
        statuses.DELETION_FAILED,
      );
  }

  if (feed?.is_paused) {
    return createFeedStatusUiObject(
      'Not Syncing',
      'bg-warning-accent',
      'bg-warning-background-weak',
      'text-warning-text-strong',
      statuses.PAUSED,
    );
  }

  /* Google Sheets cannot be in a pending state. */
  if (
    feed?.integration.type === GOOGLE_SHEETS_DATA_SOURCE_TYPE &&
    ![
      REQUIRED_FEED_STATUS,
      CONNECTION_CHECK_ERROR_STATUS,
      RECORD_LIMIT_EXCEEDED_STATUS,
      SOURCE_FAILED_STATUS,
      DELETING_FEED_STATUS,
      DELETION_FAILED_FEED_STATUS,
    ].includes(feed?.status)
  ) {
    const missingRequired = sources.some(
      (source) => source.properties?.row_errors?.missing_required > 0,
    );

    if (missingRequired) {
      return createFeedStatusUiObject(
        'Warning',
        'bg-warning-accent',
        'bg-warning-background-weak',
        'text-warning-text-strong',
        statuses.WARNING,
      );
    }
    return createFeedStatusUiObject(
      'Connection Active',
      'bg-success-accent',
      'bg-success-background-weak',
      'text-success-text-strong',
      statuses.ACTIVE,
    );
  }

  /* If error or warning state, return it */
  switch (feed?.status) {
    case REQUIRED_FEED_STATUS:
      return createFeedStatusUiObject(
        'Not Synced',
        'bg-danger-accent',
        'bg-danger-background-weak',
        'text-danger-text-strong',
        statuses.REQUIRE_SOURCES_SELECTION,
      );
    case CONNECTION_CHECK_ERROR_STATUS:
    case RECORD_LIMIT_EXCEEDED_STATUS:
      return createFeedStatusUiObject(
        'Error',
        'bg-danger-accent',
        'bg-danger-background-weak',
        'text-danger-text-strong',
        statuses.ERROR,
      );
    case SOURCE_FAILED_STATUS:
      return createFeedStatusUiObject(
        'Warning',
        'bg-warning-accent',
        'bg-warning-background-weak',
        'text-warning-text-strong',
        statuses.WARNING,
      );
  }

  /* If initial sync hasn't finished yet, show setting up */
  if (
    !feed?.initial_sync_complete &&
    feed?.integration.type !== GOOGLE_SHEETS_DATA_SOURCE_TYPE
  ) {
    return createFeedStatusUiObject(
      'Setting up...',
      'bg-warning-accent',
      'bg-warning-background-weak',
      'text-warning-text-strong',
      statuses.SETTING_UP,
    );
  }

  /* Switch on last possible options: setting up or active */
  switch (feed?.status) {
    case PENDING_FEED_STATUS:
      return createFeedStatusUiObject(
        'Setting up...',
        'bg-warning-accent',
        'bg-warning-background-weak',
        'text-warning-text-strong',
        statuses.SETTING_UP,
      );
    case ACTIVE_FEED_STATUS:
      return createFeedStatusUiObject(
        'Active',
        'bg-success-accent',
        'bg-success-background-weak',
        'text-success-text-strong',
        statuses.ACTIVE,
      );
    default:
      return {};
  }
}

export default function ({ feedId }) {
  const sourcesStore = useSourcesStore();
  const feedsStore = useFeedsStore();
  const { getAwsFeedById, getAwsSourcesByFeedId } = useAwsPartnerCentral();
  const { optionalScopesFor } = useHubspotExportScope();
  // AWS Partner Central lives on an offline-partner org and isn't in the shared
  // stores, so fall back to the AWS composable for its feed/sources/status.
  const feed = computed(
    () => feedsStore.getFeedById(feedId) ?? getAwsFeedById(feedId),
  );
  const sources = computed(() => {
    const ownSources = sourcesStore.getSourcesByFeedId(feedId);
    return ownSources.length ? ownSources : getAwsSourcesByFeedId(feedId);
  });

  const feedStatus = computed(() => getFeedStatusUi(feed.value, sources.value));

  const crmLinkInfo = computed(() => {
    return {
      text:
        feed.value?.nickname ||
        returnTrimmedDomain(feed.value?.external_base_url),
      url: feed.value?.external_base_url,
    };
  });

  /* Other feed booleans */
  const feedDeletionFailed = computed(
    () => feedStatus.value.type === statuses.DELETION_FAILED,
  );
  const feedHasErrorMessage = computed(
    () => feedIsErrored.value && feed.value?.error_message,
  );
  const feedAddSourceDisabled = computed(
    () => feed.value?.status === RECORD_LIMIT_EXCEEDED_STATUS,
  );

  const feedIsSyncing = computed(() => feed.value?.is_syncing);
  const feedIsErrored = computed(
    () => feedStatus.value.type === statuses.ERROR,
  );
  const feedIsWarning = computed(
    () => feedStatus.value.type === statuses.WARNING,
  );
  const feedIsDeleting = computed(
    () => feedStatus.value.type === statuses.DELETING,
  );
  const feedIsActive = computed(
    () => feedStatus.value.type === statuses.ACTIVE,
  );
  const feedIsPaused = computed(
    () => feedStatus.value.type === statuses.PAUSED,
  );

  /* Reauthorize Feed */
  const loading = ref(false);
  const reauthorize = reauthorizeFeed(loading, optionalScopesFor);

  return {
    feedStatus,
    feedIsSyncing,
    feedIsErrored,
    feedIsDeleting,
    feedIsWarning,
    feedIsPaused,
    feedDeletionFailed,
    feedIsActive,
    feedHasErrorMessage,
    feedAddSourceDisabled,
    reauthorize,
    feedIsReauthorizing: loading,
    statuses,
    crmLinkInfo,
    sources,
  };
}
